import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Save, ArrowLeft, Trash2 } from "lucide-react";

const DEFAULT_GENDER_OPTIONS = ["Boys", "Girls", "Coed"];
const GRADE_FALLBACK_OPTIONS = Array.from({ length: 10 }, (_, i) => String(i + 3)); // 3..12

export default function EditTeam() {
  const { programId, teamId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programRegistrationFormat, setProgramRegistrationFormat] = useState("age");
  const [programLevelOptions, setProgramLevelOptions] = useState([]);
  const [programGenderOptions, setProgramGenderOptions] = useState(DEFAULT_GENDER_OPTIONS);
  const [parentProgramId, setParentProgramId] = useState(null);
  const [programAgeGroups, setProgramAgeGroups] = useState([]);
  const [programBoysAges, setProgramBoysAges] = useState([]);
  const [programGirlsAges, setProgramGirlsAges] = useState([]);
  
  // Initialize form state matching your database schema
  const [formData, setFormData] = useState({
    name: "",
    location_address: "",
    age: "",
    gender: "",
  });

  // 1. Fetch existing team data on mount
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const { data, error } = await supabase
          .from("teams")
          .select(`
            *,
            programs (
              id,
              registration_format,
              age_groups,
              boys_ages_offered,
              girls_ages_offered
            )
          `)
          .eq("id", teamId)
          .single();

        if (error) throw error;

        // Populate form with existing data
        setFormData({
            name: data.name || "",
            location_address: data.location_address || "",
            age: data.age !== null && data.age !== undefined ? String(data.age) : "",
            gender: data.gender || ""
        });

        const program = data.programs;
        setParentProgramId(program?.id || data.program_id || null);
        const registrationFormat = program?.registration_format || "age";
        setProgramRegistrationFormat(registrationFormat);

        const levels = [
          ...(program?.age_groups || []),
          ...(program?.boys_ages_offered || []),
          ...(program?.girls_ages_offered || []),
        ]
          .map((value) => String(value).trim())
          .filter((value) => value !== "");
        setProgramAgeGroups(Array.from(new Set(levels)));
        setProgramBoysAges((program?.boys_ages_offered || []).map((value) => String(value).trim()).filter(Boolean));
        setProgramGirlsAges((program?.girls_ages_offered || []).map((value) => String(value).trim()).filter(Boolean));
        const uniqueLevels = Array.from(new Set(levels)).sort((a, b) => Number(a) - Number(b));
        const effectiveLevels = registrationFormat === "grade"
          ? Array.from(new Set([...GRADE_FALLBACK_OPTIONS, ...uniqueLevels])).sort((a, b) => Number(a) - Number(b))
          : uniqueLevels;
        setProgramLevelOptions(effectiveLevels);

        const hasBoys = (program?.boys_ages_offered || []).length > 0;
        const hasGirls = (program?.girls_ages_offered || []).length > 0;
        if (hasBoys && hasGirls) setProgramGenderOptions(["Boys", "Girls", "Coed"]);
        else if (hasBoys) setProgramGenderOptions(["Boys"]);
        else if (hasGirls) setProgramGenderOptions(["Girls"]);
        else setProgramGenderOptions(DEFAULT_GENDER_OPTIONS);
      } catch (err) {
        console.error("Error fetching team:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId]);

  // 2. Handle Input Changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 3. Update Team in Database
  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const numericAge = formData.age === "" ? null : Number(formData.age);
      if (formData.age !== "" && Number.isNaN(numericAge)) {
        throw new Error("Team age/grade must be a valid number.");
      }

      const updatePayload = {
        name: formData.name,
        location_address: formData.location_address,
        age: numericAge,
        gender: formData.gender,
      };
      const { error } = await supabase
        .from("teams")
        .update(updatePayload)
        .eq("id", teamId);

      if (error) throw error;

      if (numericAge !== null && parentProgramId) {
        const nextLevel = String(numericAge);
        const existing = (programAgeGroups || []).map((value) => String(value).trim()).filter(Boolean);
        const existingBoys = (programBoysAges || []).map((value) => String(value).trim()).filter(Boolean);
        const existingGirls = (programGirlsAges || []).map((value) => String(value).trim()).filter(Boolean);

        const nextAgeGroups = Array.from(new Set([...existing, nextLevel])).sort((a, b) => Number(a) - Number(b));
        const shouldAddBoys = formData.gender === "Boys" || formData.gender === "Coed";
        const shouldAddGirls = formData.gender === "Girls" || formData.gender === "Coed";
        const nextBoys = shouldAddBoys
          ? Array.from(new Set([...existingBoys, nextLevel])).sort((a, b) => Number(a) - Number(b))
          : existingBoys;
        const nextGirls = shouldAddGirls
          ? Array.from(new Set([...existingGirls, nextLevel])).sort((a, b) => Number(a) - Number(b))
          : existingGirls;

        const ageGroupsChanged = nextAgeGroups.join("|") !== existing.join("|");
        const boysChanged = nextBoys.join("|") !== existingBoys.join("|");
        const girlsChanged = nextGirls.join("|") !== existingGirls.join("|");

        if (ageGroupsChanged || boysChanged || girlsChanged) {
          const { error: programUpdateError } = await supabase
            .from("programs")
            .update({
              age_groups: nextAgeGroups,
              boys_ages_offered: nextBoys,
              girls_ages_offered: nextGirls
            })
            .eq("id", parentProgramId);
          if (programUpdateError) throw programUpdateError;
          setProgramAgeGroups(nextAgeGroups);
          setProgramBoysAges(nextBoys);
          setProgramGirlsAges(nextGirls);
          setProgramLevelOptions((prev) =>
            Array.from(new Set([...(prev || []), nextLevel])).sort((a, b) => Number(a) - Number(b))
          );
        }
      }
      
      alert("Team updated successfully!");
    } catch (err) {
      alert("Error updating team: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // 4. (Optional) Delete Team Handler
  const handleDelete = async () => {
    if(!window.confirm("Are you sure? This cannot be undone.")) return;
    
    try {
        const { error } = await supabase.from('teams').delete().eq('id', teamId);
        if(error) throw error;
        // Redirect to program root or another team?
        navigate(`/admin`); 
    } catch(err) {
        alert(err.message);
    }
  }

  if (loading) return <div className="p-8 text-center">Loading team details...</div>;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
            <button 
                onClick={() => navigate(`/admin/program/${programId}/team/${teamId}/dashboard`)}
                className="flex items-center text-gray-600 hover:text-blue-600"
            >
                <ArrowLeft size={20} className="mr-2"/> Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Edit Team Settings</h1>
        </div>

        <form onSubmit={handleUpdate} className="space-y-6">
          
          {/* Identity Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold mb-1">Team Name</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Varsity, JV, U18"
              />
            </div>
          </div>

          {/* Location Section */}
          <div className="border-t pt-4">
             <h2 className="text-lg font-semibold mb-4 text-gray-700">Team Demographics</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Team {programRegistrationFormat === "grade" ? "Grade" : "Age"}
                </label>
                <select
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                >
                  <option value="">
                    Select {programRegistrationFormat === "grade" ? "grade" : "age"}...
                  </option>
                  {programLevelOptions.map((level) => (
                    <option key={level} value={level}>
                      {programRegistrationFormat === "grade" ? `Grade ${level}` : `${level}U`}
                    </option>
                  ))}
                  {formData.age && !programLevelOptions.includes(String(formData.age)) ? (
                    <option value={formData.age}>{String(formData.age)}</option>
                  ) : null}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  For multi-age teams (for example, high school varsity), select the primary anchor level here (typically Grade 12) and keep the full span (for example 9-12) on the parent program.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Team Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select gender...</option>
                  {programGenderOptions.map((genderOption) => (
                    <option key={genderOption} value={genderOption}>{genderOption}</option>
                  ))}
                </select>
              </div>
             </div>
          </div>

          {/* Logistics Section */}
          <div className="border-t pt-4">
             <h2 className="text-lg font-semibold mb-4 text-gray-700">Logistics</h2>
             <div>
              <label className="block text-sm font-semibold mb-1">Home Location Address</label>
              <input
                name="location_address"
                value={formData.location_address}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-6 border-t mt-6">
            <button
                type="button"
                onClick={handleDelete}
                className="flex items-center text-red-500 hover:text-red-700 px-4 py-2"
            >
                <Trash2 size={18} className="mr-2" /> Delete Team
            </button>

            <button
              type="submit"
              disabled={saving}
              className="flex items-center bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700 transition disabled:opacity-50"
            >
              <Save size={18} className="mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
